import React, { useEffect, useState } from 'react';
import GoogleWordmark from './GoogleWordmark';
import StarRating from './StarRating';
import {
  fetchGoogleReviews,
  getAuthorInitials,
  getReviewRelativeTime,
} from '../utils/googleReviewsApi';

function ReviewAvatar({ author, photoUrl }) {
  const [failed, setFailed] = useState(false);

  if (photoUrl && !failed) {
    return (
      <img
        className="tsr-google-reviews-carousel__avatar-img"
        src={photoUrl}
        alt={author}
        loading="lazy"
        referrerPolicy="no-referrer"
        onError={() => setFailed(true)}
      />
    );
  }

  return (
    <span className="tsr-google-reviews-carousel__avatar-initials">
      {getAuthorInitials(author)}
    </span>
  );
}

function ReviewSlide({ review }) {
  const relativeTime = getReviewRelativeTime(review);

  return (
    <article className="tsr-google-reviews-carousel__card">
      <StarRating rating={review.rating} size="lg" className="tsr-google-reviews-carousel__stars" />
      {review.text ? <p className="tsr-google-reviews-carousel__text">{review.text}</p> : null}
      <div className="tsr-google-reviews-carousel__avatar">
        <ReviewAvatar author={review.author} photoUrl={review.profilePhotoUrl} />
      </div>
      <div className="tsr-google-reviews-carousel__author-row">
        <span className="tsr-google-reviews-carousel__author">{review.author}</span>
        <svg
          className="tsr-google-reviews-carousel__verified"
          viewBox="0 0 24 24"
          width="16"
          height="16"
          aria-label="Verified"
        >
          <path
            fill="#1a73e8"
            d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1.25 14.5L6.5 12.25l1.41-1.41 2.84 2.84 6.34-6.34 1.41 1.41-7.75 7.75z"
          />
        </svg>
      </div>
      {relativeTime ? (
        <span className="tsr-google-reviews-carousel__time">{relativeTime}</span>
      ) : null}
      <GoogleWordmark className="tsr-google-reviews-carousel__wordmark" />
    </article>
  );
}

function HomeGoogleReviewsCarousel() {
  const [data, setData] = useState(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    fetchGoogleReviews()
      .then((result) => {
        if (active) setData(result);
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, []);

  if (!loading && !data) {
    return null;
  }

  const reviews = data?.reviews || [];
  const hasMultiple = reviews.length > 1;

  const goPrev = () => {
    setActiveIndex((index) => (index - 1 + reviews.length) % reviews.length);
  };

  const goNext = () => {
    setActiveIndex((index) => (index + 1) % reviews.length);
  };

  return (
    <section className="tsr-google-reviews-carousel" aria-label="Google reviews">
      <h2 className="tsr-google-reviews-carousel__title">What Our Customers Say</h2>

      <div className="tsr-google-reviews-carousel__frame">
        {hasMultiple ? (
          <button
            type="button"
            className="tsr-google-reviews-carousel__arrow tsr-google-reviews-carousel__arrow--prev"
            onClick={goPrev}
            aria-label="Previous review"
          >
            &#8249;
          </button>
        ) : null}

        <div className="tsr-google-reviews-carousel__viewport">
          {loading ? (
            <div className="tsr-google-reviews-carousel__card tsr-google-reviews-carousel__card--loading" />
          ) : (
            <div
              className="tsr-google-reviews-carousel__track"
              style={{ transform: `translateX(-${activeIndex * 100}%)` }}
            >
              {reviews.map((review, index) => (
                <div
                  key={`${review.author}-${index}`}
                  className="tsr-google-reviews-carousel__slide"
                >
                  <ReviewSlide review={review} />
                </div>
              ))}
            </div>
          )}
        </div>

        {hasMultiple ? (
          <button
            type="button"
            className="tsr-google-reviews-carousel__arrow tsr-google-reviews-carousel__arrow--next"
            onClick={goNext}
            aria-label="Next review"
          >
            &#8250;
          </button>
        ) : null}
      </div>

      {hasMultiple && !loading ? (
        <div className="tsr-google-reviews-carousel__dots" role="tablist" aria-label="Review slides">
          {reviews.map((review, index) => (
            <button
              key={`dot-${review.author}-${index}`}
              type="button"
              className={`tsr-google-reviews-carousel__dot ${
                index === activeIndex ? 'tsr-google-reviews-carousel__dot--active' : ''
              }`}
              onClick={() => setActiveIndex(index)}
              aria-label={`Show review ${index + 1}`}
              aria-current={index === activeIndex ? 'true' : undefined}
            />
          ))}
        </div>
      ) : null}
    </section>
  );
}

export default HomeGoogleReviewsCarousel;
